import { find, hasProp, isPlainObject, isString, pipe } from 'remeda'

export type CompanyFormatSettings = Readonly<{
  currencyCode: string
  dateFormat: string
  locale: string
  showCents: boolean
  timeFormat: string
  timeZone: string
}>

export type CompanyDateFormatSettings = Pick<
  CompanyFormatSettings,
  'dateFormat' | 'locale'
>

type CalendarDate = Readonly<{
  day: number
  instant: Date
  month: number
  year: number
}>

type DateToken = Readonly<{
  format: string
  padding: '' | '-' | '0' | '_'
  width?: number
}>

type IsoWeek = Readonly<{
  week: number
  year: number
}>

const DEFAULT_SETTINGS: CompanyFormatSettings = {
  currencyCode: 'USD',
  dateFormat: '%d %B %Y',
  locale: 'en',
  showCents: true,
  timeFormat: '%H:%M',
  timeZone: 'UTC'
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const DATE_TOKEN_PATTERN = /%(?:[-_0]?\d*)?[A-Za-z%]/g
const PARSED_DATE_TOKEN_PATTERN = /^%([-_0]?)(\d*)([A-Za-z%])$/

const stringSetting = (
  value: unknown,
  key: keyof CompanyFormatSettings,
  fallback: string
): string =>
  isPlainObject(value) && hasProp(value, key) && isString(value[key])
    ? value[key]
    : fallback

const booleanSetting = (
  value: unknown,
  key: keyof CompanyFormatSettings,
  fallback: boolean
): boolean =>
  isPlainObject(value) && hasProp(value, key) && typeof value[key] === 'boolean'
    ? value[key]
    : fallback

const parseSettings = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch (_error: unknown) {
    return undefined
  }
}

export const readCompanyFormatSettings = (
  root: HTMLElement = document.documentElement
): CompanyFormatSettings => {
  const fallback = {
    ...DEFAULT_SETTINGS,
    locale: root.lang || DEFAULT_SETTINGS.locale
  }
  const value = parseSettings(root.dataset.formatSettings ?? '')

  return {
    currencyCode: stringSetting(value, 'currencyCode', fallback.currencyCode),
    dateFormat: stringSetting(value, 'dateFormat', fallback.dateFormat),
    locale: stringSetting(value, 'locale', fallback.locale),
    showCents: booleanSetting(value, 'showCents', fallback.showCents),
    timeFormat: stringSetting(value, 'timeFormat', fallback.timeFormat),
    timeZone: stringSetting(value, 'timeZone', fallback.timeZone)
  }
}

const parseCalendarDate = (value: string): CalendarDate | undefined => {
  const match = ISO_DATE_PATTERN.exec(value)

  if (!match) {
    return undefined
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const instant = new Date(0)
  instant.setUTCFullYear(year, month - 1, day)
  instant.setUTCHours(12, 0, 0, 0)

  return instant.getUTCFullYear() === year &&
    instant.getUTCMonth() === month - 1 &&
    instant.getUTCDate() === day
    ? { day, instant, month, year }
    : undefined
}

const parseDateToken = (value: string): DateToken | undefined => {
  const match = PARSED_DATE_TOKEN_PATTERN.exec(value)

  if (!match) {
    return undefined
  }

  return {
    format: match[3],
    padding: match[1] as DateToken['padding'],
    width: match[2] === '' ? undefined : Number(match[2])
  }
}

const padToken = (
  value: string | number,
  token: DateToken,
  defaultWidth: number,
  defaultFill: ' ' | '0' = '0'
): string => {
  const stringValue = String(value)
  const width = token.width ?? defaultWidth

  if (token.padding === '-' || width === 0) {
    return stringValue
  }

  const fill =
    token.padding === '_' ? ' ' : token.padding === '0' ? '0' : defaultFill

  return stringValue.padStart(width, fill)
}

const localizedPart = (
  date: CalendarDate,
  locale: string,
  type: Intl.DateTimeFormatPartTypes,
  options: Intl.DateTimeFormatOptions
): string => {
  const format = (resolvedLocale: string): string | undefined =>
    pipe(
      new Intl.DateTimeFormat(resolvedLocale, {
        ...options,
        timeZone: 'UTC'
      }).formatToParts(date.instant),
      find((part) => part.type === type)
    )?.value

  try {
    return format(locale) ?? ''
  } catch (_error: unknown) {
    return format(DEFAULT_SETTINGS.locale) ?? ''
  }
}

const dayOfYear = ({ day, month, year }: CalendarDate): number => {
  const start = Date.UTC(year, 0, 1)
  const current = Date.UTC(year, month - 1, day)
  return Math.floor((current - start) / 86_400_000) + 1
}

const isoWeek = ({ day, instant, month, year }: CalendarDate): IsoWeek => {
  const dayNumber = Math.floor(Date.UTC(year, month - 1, day) / 86_400_000)
  const weekDay = instant.getUTCDay() || 7
  const thursdayDayNumber = dayNumber + 4 - weekDay
  const isoYear = new Date(thursdayDayNumber * 86_400_000).getUTCFullYear()
  const isoYearStart = Math.floor(Date.UTC(isoYear, 0, 1) / 86_400_000)

  return {
    week: Math.floor((thursdayDayNumber - isoYearStart) / 7) + 1,
    year: isoYear
  }
}

const dateTokenValue = (
  value: string,
  date: CalendarDate,
  locale: string
): string | undefined => {
  const token = parseDateToken(value)

  if (!token || (token.width !== undefined && token.width > 100)) {
    return undefined
  }

  switch (token.format) {
    case '%':
      return padToken('%', token, 0)
    case 'Y':
      return padToken(date.year, token, 4)
    case 'y':
      return padToken(date.year % 100, token, 2)
    case 'C':
      return padToken(Math.floor(date.year / 100), token, 2)
    case 'G':
      return padToken(isoWeek(date).year, token, 4)
    case 'g':
      return padToken(isoWeek(date).year % 100, token, 2)
    case 'm':
      return padToken(date.month, token, 2)
    case 'B':
      return padToken(
        localizedPart(date, locale, 'month', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }),
        token,
        0,
        ' '
      )
    case 'b':
    case 'h':
      return padToken(
        localizedPart(date, 'en', 'month', { month: 'short' }),
        token,
        0,
        ' '
      )
    case 'd':
      return padToken(date.day, token, 2)
    case 'e':
      return padToken(date.day, token, 2, ' ')
    case 'j':
      return padToken(dayOfYear(date), token, 3)
    case 'A':
      return padToken(
        localizedPart(date, 'en', 'weekday', { weekday: 'long' }),
        token,
        0,
        ' '
      )
    case 'a':
      return padToken(
        localizedPart(date, 'en', 'weekday', { weekday: 'short' }),
        token,
        0,
        ' '
      )
    case 'q':
      return padToken(Math.ceil(date.month / 3), token, 0)
    case 'u': {
      const weekDay = date.instant.getUTCDay()
      return padToken(weekDay === 0 ? 7 : weekDay, token, 0)
    }
    case 'V':
      return padToken(isoWeek(date).week, token, 2)
    case 'w':
      return padToken(date.instant.getUTCDay(), token, 0)
    case 'D':
      return padToken(
        `${String(date.month).padStart(2, '0')}/${String(date.day).padStart(
          2,
          '0'
        )}/${String(date.year % 100).padStart(2, '0')}`,
        token,
        0
      )
    case 'F':
    case 'x':
      return padToken(
        `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(
          2,
          '0'
        )}-${String(date.day).padStart(2, '0')}`,
        token,
        0
      )
    default:
      return undefined
  }
}

export const formatDate = (
  value: string,
  settings: CompanyDateFormatSettings
): string => {
  if (value === '') {
    return ''
  }

  const date = parseCalendarDate(value)

  if (!date) {
    return value
  }

  const tokens = settings.dateFormat.match(DATE_TOKEN_PATTERN) ?? []
  const unsupportedToken = pipe(
    tokens,
    find((token) => dateTokenValue(token, date, settings.locale) === undefined)
  )

  if (unsupportedToken !== undefined) {
    return value
  }

  return settings.dateFormat.replace(
    DATE_TOKEN_PATTERN,
    (token) => dateTokenValue(token, date, settings.locale) ?? token
  )
}
